import {ILanguage, SortDirection} from '../types/common'
import {useMemo} from 'react'

interface usePaginationProps {
    totalPages: number
    siblingCount?: number
    activePage: number
}

export const DOTS = 0

export const COLUMNS = ['1D', '1M', '12M', '36M', '60M', 'MAX']

export function isEmpty(obj: Record<string, any> = {}): boolean {
    return Object.keys(obj).length === 0
}

export function isString(value: any): value is string {
    return typeof value === 'string' || value instanceof String
}

export function isNumber(value: any): value is number {
    return typeof value === 'number' && !isNaN(value)
}

export function isBoolean(value: any): value is boolean {
    return value === true || value === false
}

export function isNil(value: any): value is null | undefined {
    return typeof value === 'undefined' || value === null
}

export function isDateString(value: any): boolean {
    if (!isString(value)) return false
    return /^\d{2}-\d{2}-\d{4}$/.test(value)
}

export function isArray(value: any) {
    return value instanceof Array
}

export function convertDateString(value: string): string {
    return value.substr(6, 4) + value.substr(3, 2) + value.substr(0, 2)
}

export function toLower(value: any): string | any {
    if (isString(value)) {
        return value.toLowerCase()
    }
    return value
}

export function convertType(value: any): string | any {
    if (isNumber(value)) {
        return value.toString()
    }

    if (isDateString(value)) {
        return convertDateString(value)
    }

    if (isBoolean(value)) {
        return value ? '1' : '-1'
    }

    if (isString(value)) {
        return value.toLocaleLowerCase()
    }

    return value
}

const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // Escape special characters
}

export function filterRows(rows: any[], filters: Record<string, any>): any[] {
    if (isEmpty(filters)) return rows

    return rows.filter((row) => {
        return Object.keys(filters).every((accessor) => {
            const value = row[accessor]
            const searchValue = filters[accessor]
            if (isArray(searchValue) && searchValue.length > 0) {
                if (isArray(value) && value.length > 0) {
                    return value.some((vItem) => searchValue.includes(vItem))
                }
                return searchValue.includes(value)
            }

            if (isString(value) && isString(searchValue)) {
                // return toLower(value).includes(toLower(searchValue))
                try {
                    const regex = new RegExp(
                        escapeRegExp(searchValue.replace(/\s+/g, ' ').trim()).split(' ').join('|'),
                        'i'
                    )
                    return regex.test(value)
                } catch (error) {
                    console.error('Invalid keyword:', error)
                    return false
                }
            }

            if (isBoolean(value)) {
                return (searchValue === true && value) || (searchValue === false && !value)
            }

            if (isNumber(value)) {
                return value == searchValue
            }

            return false
        })
    })
}

interface SortOptions {
    sortDir: SortDirection
    sortCol: string
}

export function sortRows<Fund>(rows: Fund[], sort: SortOptions, language: ILanguage, favorites: any[]): Fund[] {
    return rows.sort((a, b) => {
        const {sortDir, sortCol} = sort

        // Check if either item is a favorite
        const aIsFavorite = favorites.includes(a['id'])
        const bIsFavorite = favorites.includes(b['id'])

        // Prioritize favorites
        if (aIsFavorite && !bIsFavorite) return -1 // a comes first
        if (!aIsFavorite && bIsFavorite) return 1 // b comes first

        // Handle nil values

        if (isNil(a[sortCol]) && isNil(b[sortCol])) {
            const aSecondaryLocale = convertType(a['name'])
            const bSecondaryLocale = convertType(b['name'])
            return aSecondaryLocale.localeCompare(bSecondaryLocale, language) //always ASC
        } // Both are nil

        if (isNil(a[sortCol])) return 1 // a is nil, b comes first
        if (isNil(b[sortCol])) return -1 // b is nil, a comes first

        let primaryComparison = 0

        if (sortCol === 'name') {
            const aLocale = convertType(a[sortCol])
            const bLocale = convertType(b[sortCol])
            primaryComparison = aLocale.localeCompare(bLocale, language, {numeric: isNumber(b[sortCol])})
        } else {
            const aValue = isNumber(a[sortCol]) ? a[sortCol] : parseFloat(a[sortCol])
            const bValue = isNumber(b[sortCol]) ? b[sortCol] : parseFloat(b[sortCol])
            primaryComparison = aValue - bValue
        }

        // If primary values are equal, apply secondary sorting if provided

        if (primaryComparison === 0) {
            const aSecondaryLocale = convertType(a['name'])

            const bSecondaryLocale = convertType(b['name'])

            return aSecondaryLocale.localeCompare(bSecondaryLocale, language) //always ASC
        }

        // Return primary comparison based on sort direction

        return sortDir === 'ASC' ? primaryComparison : -primaryComparison // Reverse for DESC
    })
}

export function paginateRows(sortedRows: any[], activePage: number, rowsPerPage: number): any[] {
    return [...sortedRows].slice((activePage - 1) * rowsPerPage, activePage * rowsPerPage)
}

const range = (start: number, end: number): number[] => {
    const length = end - start + 1
    return Array.from({length}, (_, idx) => idx + start)
}

export const usePagination = ({totalPages, siblingCount = 1, activePage}: usePaginationProps): number[] => {
    const paginationRange = useMemo(() => {
        const totalPageNumbers = siblingCount + 6

        if (totalPageNumbers >= totalPages) {
            return range(1, totalPages)
        }

        const leftSiblingIndex = Math.max(activePage - siblingCount, 1)
        const rightSiblingIndex = Math.min(activePage + siblingCount, totalPages)

        const shouldShowLeftDots = leftSiblingIndex > 3
        const shouldShowRightDots = rightSiblingIndex < totalPages - 2

        const firstPageIndex = 1
        const lastPageIndex = totalPages

        if (!shouldShowLeftDots && shouldShowRightDots) {
            const leftItemCount = 3 + 2 * siblingCount
            const leftRange = range(1, leftItemCount)

            return [...leftRange, DOTS, totalPages]
        }

        if (shouldShowLeftDots && !shouldShowRightDots) {
            const rightItemCount = 3 + 2 * siblingCount
            const rightRange = range(totalPages - rightItemCount + 1, totalPages)
            return [firstPageIndex, DOTS, ...rightRange]
        }

        if (shouldShowLeftDots && shouldShowRightDots) {
            const middleRange = range(leftSiblingIndex, rightSiblingIndex)
            return [firstPageIndex, DOTS, ...middleRange, DOTS, lastPageIndex]
        }
    }, [totalPages, siblingCount, activePage])

    return paginationRange
}

export const compareArrays = (a: string[], b: string[]) => {
    if (a.length !== b.length) return false
    else {
        // sort before comparison
        a.sort()
        b.sort()
        // Comparing each element of your array
        for (let i = 0; i < a.length; i++) {
            if (a[i] !== b[i]) {
                return false
            }
        }
        return true
    }
}

/**
 * Convert date to 'yyyy-MM-dd' formatted string
 * Fix timezone offset issue
 * @param date
 * @return string|null
 */
export const yyyyMMdd = function (date: Date | null | undefined): string | null | undefined {
    if (date !== null && date != undefined) {
        const offset = date.getTimezoneOffset()
        return new Date(date.getTime() - offset * 60 * 1000).toISOString().split('T')[0]
    }
    return null
}

export const invRor = {
    '1D': 'oneDay',
    '1M': 'oneMonth',
    '3M': 'threeMonths',
    '6M': 'sixMonths',
    '12M': 'oneYear',
    '36M': 'threeYears',
    '60M': 'fiveYears',
    YTD: 'yearToDate',
    MAX: 'max',
}

export const getValueForDate = (
    fundQuotation: Array<{date: string; value: number}>,
    date: string | null | undefined,
    defaultValue: any
) => {
    const entry = fundQuotation.find((item) => item.date === date)
    return entry ? entry.value : defaultValue
}
